// src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { AdminProvider } from "./context/AdminContext";
import { db } from "./firebase/config";
import { doc, getDoc } from "firebase/firestore";
import LoginPage from "./pages/LoginPage";
import Home from "./pages/Home";
import SubscribePage from "./pages/SubscribePage";
import MyPlan from "./pages/MyPlan";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Help from "./pages/Help";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ShippingAndDelivery from "./pages/ShippingAndDelivery";
import CancellationRefund from "./pages/CancellationRefund";
import ContactUs from "./pages/ContactUs";
import Footer from "./components/Footer";
import BottomNav from "./components/BottomNav";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminCustomerProfile from "./pages/admin/AdminCustomerProfile";
import AdminDeliveryTracker from "./pages/admin/AdminDeliveryTracker";
import AdminRoute from "./components/AdminRoute";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminDeliveryPartners from "./pages/admin/AdminDeliveryPartners";
import AdminAssignDeliveries from "./pages/admin/AdminAssignDeliveries";
import DeliveryPartnerPanel from "./pages/admin/DeliveryPartnerPanel";
import DeliveryPartnerLogin from "./pages/admin/DeliveryPartnerLogin";
import DeliveryPartnerRegistration from "./pages/admin/DeliveryPartnerRegistration";
import DeliveryPartnerPending from "./pages/admin/DeliveryPartnerPending";
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import AssignedDeliveriesDashboard from "./pages/partner/AssignedDeliveriesDashboard";
import AdminCoupons from "./pages/admin/AdminCoupons";
import useSaveFcmToken from "./hooks/useSaveFcmToken";
import DeliveryPartnerRejected from "./pages/admin/DeliveryPartnerRejected";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminCustomOrders from "./pages/admin/AdminCustomOrders";
import AdminCustomOrdersEnhanced from "./pages/admin/AdminCustomOrdersEnhanced";
import AdminSubscriptionPlans from "./pages/admin/AdminSubscriptionPlans";
import AdminBonusSettings from "./pages/admin/AdminBonusSettings";
import AdminManagement from "./pages/admin/AdminManagement";
import AdminNotifications from "./pages/admin/AdminNotifications";
import { messaging } from "./firebase/config";
import { onMessage } from "firebase/messaging";
import AdminProductInterest from "./pages/admin/AdminProductInterest";
import AdminCustomerCalendar from "./pages/admin/AdminCustomerCalendar";
import AdminSubscriptionReports from "./pages/admin/AdminSubscriptionReports";

// Layout with optional footer + nav
const Layout = ({ children, showFooter = true, showNav = true }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 pb-20">{children}</div>
      {showFooter && <Footer />}
      {/* Show BottomNav only on mobile */}
      {showNav && <div className="md:hidden"><BottomNav /></div>}
    </div>
  );
};

// Protected Route logic
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Delivery Partner Route Guard
const DeliveryPartnerRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      if (user && role === "deliveryPartner") {
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            setStatus(userSnap.data().status);
          } else {
            setStatus(null);
          }
        } catch (err) {
          setStatus(null);
        }
      }
      setChecking(false);
    };
    if (user && role === "deliveryPartner") {
      fetchStatus();
    } else {
      setChecking(false);
    }
  }, [user, role]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/delivery-partner/login" replace />;
  if (role !== "deliveryPartner" || status !== "approved") {
    // Redirect to registration if not approved
    return <Navigate to="/delivery-partner/register" replace />;
  }
  return children;
};

function App() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planValidationError, setPlanValidationError] = useState(null);

  // Auto-save FCM token when user is authenticated
  useSaveFcmToken();

  // Validate and enhance plan data when selected
  const handlePlanSelection = async (plan) => {
    try {
      setPlanValidationError(null);
      
      // Validate required fields
      if (!plan || !plan.name || !plan.price) {
        setPlanValidationError("Invalid plan data. Please try again.");
        return;
      }

      // Ensure price is a number
      const validatedPlan = {
        ...plan,
        price: typeof plan.price === 'string' ? parseInt(plan.price.replace(/[^\d]/g, "")) : Number(plan.price),
        rotisPerDay: plan.rotisPerDay || 2,
        id: plan.id || null
      };

      // Validate price
      if (isNaN(validatedPlan.price) || validatedPlan.price <= 0) {
        setPlanValidationError("Invalid plan price. Please try again.");
        return;
      }

      // If plan has an ID, fetch the latest version from Firestore
      if (validatedPlan.id) {
        try {
          const planDoc = await getDoc(doc(db, "subscription_plans", validatedPlan.id));
          if (planDoc.exists()) {
            const latestPlan = planDoc.data();
            // Use the latest data from Firestore
            validatedPlan.price = latestPlan.price;
            validatedPlan.name = latestPlan.name;
            validatedPlan.rotisPerDay = latestPlan.rotisPerDay;
            validatedPlan.details = latestPlan.details || plan.details;
            validatedPlan.note = latestPlan.note || plan.note;
          }
        } catch (error) {
          console.warn("Could not fetch latest plan data:", error);
          // Continue with the plan data we have
        }
      }

      setSelectedPlan(validatedPlan);
    } catch (error) {
      console.error("Error validating plan:", error);
      setPlanValidationError("Error processing plan. Please try again.");
    }
  };

  // Clear plan validation error when plan is cleared
  const handlePlanComplete = () => {
    setSelectedPlan(null);
    setPlanValidationError(null);
  };

  // Foreground notification handler
  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("🔔 Foreground Notification:", payload);
      alert(`${payload.notification?.title}\n${payload.notification?.body}`);
    });
    return () => unsubscribe();
  }, []);

  return (
    <AdminProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/delivery-partner/login" element={<DeliveryPartnerLogin />} />
        <Route path="/delivery-partner/register" element={<DeliveryPartnerRegistration />} />
        <Route path="/delivery-partner/pending" element={<DeliveryPartnerPending />} />
        <Route path="/delivery-partner/rejected" element={<DeliveryPartnerRejected />} />
        <Route 
          path="/admin/dashboard" 
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/subscriptions" 
          element={
            <AdminRoute>
              <AdminSubscriptions />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/customers" 
          element={
            <AdminRoute>
              <AdminCustomers />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/customers/:customerId" 
          element={
            <AdminRoute>
              <AdminCustomerProfile />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/customers/:customerId/calendar" 
          element={
            <AdminRoute>
              <AdminCustomerCalendar />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/delivery-tracker" 
          element={
            <AdminRoute>
              <AdminDeliveryTracker />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/analytics" 
          element={
            <AdminRoute>
              <AdminAnalytics />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/delivery-partners" 
          element={
            <AdminRoute>
              <AdminDeliveryPartners />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/assign-deliveries" 
          element={
            <AdminRoute>
              <AdminAssignDeliveries />
            </AdminRoute>
          } 
        />
        <Route path="/delivery-partner" element={<DeliveryPartnerPanel />} />
        <Route path="/partner/dashboard" element={<DeliveryPartnerRoute><PartnerDashboard /></DeliveryPartnerRoute>} />
        <Route path="/partner/deliveries" element={<AssignedDeliveriesDashboard />} />
        <Route 
          path="/admin/coupons" 
          element={
            <AdminRoute>
              <AdminCoupons />
            </AdminRoute>
          } 
        />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/custom-orders" element={<AdminRoute><AdminCustomOrders /></AdminRoute>} />
        <Route path="/admin/custom-orders-enhanced" element={<AdminRoute><AdminCustomOrdersEnhanced /></AdminRoute>} />
        <Route 
          path="/admin/plans" 
          element={
            <AdminRoute>
              <AdminSubscriptionPlans />
            </AdminRoute>
          } 
        />
        <Route path="/admin/bonus-settings" element={<AdminRoute><AdminBonusSettings /></AdminRoute>} />
        <Route 
          path="/admin/management" 
          element={
            <AdminRoute>
              <AdminManagement />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/notifications" 
          element={
            <AdminRoute>
              <AdminNotifications />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/product-interest" 
          element={
            <AdminRoute>
              <AdminProductInterest />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/reports" 
          element={
            <AdminRoute>
              <AdminSubscriptionReports />
            </AdminRoute>
          } 
        />

        {/* ✅ Home (public - no login required) */}
        <Route
          path="/"
          element={
            <Layout>
              <Home onSelectPlan={handlePlanSelection} />
            </Layout>
          }
        />

        {/* ✅ Subscribe (open for all users) */}
        <Route
          path="/subscribe"
          element={
            <Layout showFooter={true} showNav={!!user}>
              {planValidationError ? (
                <div className="min-h-screen flex items-center justify-center bg-red-50">
                  <div className="text-center p-6 bg-white rounded-lg shadow-lg">
                    <div className="text-red-600 text-2xl mb-4">⚠️</div>
                    <h3 className="text-lg font-semibold text-red-700 mb-2">Plan Selection Error</h3>
                    <p className="text-red-600 mb-4">{planValidationError}</p>
                    <button
                      onClick={() => {
                        setPlanValidationError(null);
                        navigate("/");
                      }}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                    >
                      Go Back to Plans
                    </button>
                  </div>
                </div>
              ) : (
              <SubscribePage
                user={user}
                plan={selectedPlan}
                  onComplete={handlePlanComplete}
              />
              )}
            </Layout>
          }
        />

        {/* ✅ My Plan */}
        <Route
          path="/myplan"
          element={
            <ProtectedRoute>
              <Layout>
                <MyPlan />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ✅ Profile */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ✅ Settings */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ✅ Help */}
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <Layout>
                <Help />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* 📄 Legal Pages */}
        <Route
          path="/privacy-policy"
          element={<Layout><PrivacyPolicy /></Layout>}
        />
        <Route
          path="/terms-and-conditions"
          element={<Layout><TermsAndConditions /></Layout>}
        />
        <Route
          path="/shipping-and-delivery"
          element={<Layout><ShippingAndDelivery /></Layout>}
        />
        <Route
          path="/cancellation-refund"
          element={<Layout><CancellationRefund /></Layout>}
        />
        <Route
          path="/contact-us"
          element={<Layout><ContactUs /></Layout>}
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AdminProvider>
  );
}

export default App;
